import { useState } from 'react';
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import { MdSecurity } from "react-icons/md";
import InputField from "components/fields/InputField";
import { Auth } from "aws-amplify";

const TwoFactorConfirmModal: React.FC<{
    setSwalProps: any;
    isOpen: boolean;
    onClose(): void;
    isEnabling: boolean;
    setTwoFactorEnabled: (value: boolean) => void;
    userRole: string;
    username: string;
    onOpenSetupModal?: () => void;
}> = ({
    setSwalProps,
    isOpen,
    onClose,
    isEnabling,
    setTwoFactorEnabled,
    userRole,
    username,
    onOpenSetupModal
}) => {
        const [isProcessing, setIsProcessing] = useState<boolean>(false);
        const [password, setPassword] = useState<string>("");
        const [passwordIncorrect, setPasswordIncorrect] = useState<boolean>(false);
        const action = isEnabling ? "enabled" : "disabled";

        const closeModal = () => {
            setPassword("");
            setPasswordIncorrect(false);
            setIsProcessing(false);            
            onClose();
        };

        const handleSubmit = async () => {
            try {
                setIsProcessing(true);
                setPasswordIncorrect(false);

                // Verify password first
                const userInfo = await Auth.currentAuthenticatedUser();
                await Auth.signIn(userInfo.attributes.email, password);

                // For ENABLE: Close this modal first, then open setup modal
                if (action === "enabled" && onOpenSetupModal) {
                    setPassword("");
                    setPasswordIncorrect(false);
                    setIsProcessing(false);
                    onClose();
                    // Small delay to ensure this modal is closed before opening setup modal
                    setTimeout(() => {
                        onOpenSetupModal();
                    }, 100);
                    return;
                }

                // For DISABLE: Proceed with API call
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/${userRole.toLowerCase()}/user/action/user-profile-update-two-factor-status`,
                    {
                        username: username,
                        status: action,
                        userRole: userRole,
                    }
                );

                // Close modal first, then show success message
                setTwoFactorEnabled(false);
                setPassword("");
                setPasswordIncorrect(false);
                setIsProcessing(false);
                onClose();

                setSwalProps({
                    show: true,
                    icon: "success",
                    title: "Success",
                    html: "Two-factor authentication has been disabled. Your account security has been updated.",
                    showConfirmButton: true,
                    didClose: () => {
                        setSwalProps({});
                    },
                });
            } catch (error: any) {
                setIsProcessing(false);

                // Check if error is from password verification
                if (error.code && (error.code === 'NotAuthorizedException' || error.code === 'UserNotFoundException')) {
                    setPasswordIncorrect(true);
                } else if (error.code === 'LimitExceededException' || String(error).includes('LimitExceededException')) {
                    closeModal();
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Too Many Attempts",
                        html: "Attempt limit exceeded, please try again later.",
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                        },
                    });
                } else {
                    console.error("Error updating 2FA status:", error);
                    closeModal();
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response?.data?.message || "Failed to update two-factor authentication status.",
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                        },
                    });
                }
            }
        };

        return (
            <>
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                    <MdSecurity size={20} />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {action === "enabled" ? "Enable" : "Disable"} Two-Factor Authentication
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        For security reasons, please enter your current password to proceed
                                    </p>
                                </div>
                            </div>

                            {action === "enabled" ? (
                                <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
                                    You will be guided to set up two-factor authentication. This adds an extra layer of security to your account.
                                </div>
                            ) : (
                                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                                    Disabling two-factor authentication will make your account less secure. Are you sure you want to proceed?
                                </div>
                            )}

                            <div className="mb-6">
                                <InputField
                                    disabled={isProcessing}
                                    variant="auth"
                                    extra="shadow-sm"
                                    label="Password"
                                    required={true}
                                    placeholder=""
                                    id="password"
                                    type="password"
                                    autoComplete="off"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordIncorrect(false);
                                    }}
                                />
                                {passwordIncorrect && (
                                    <p className="mt-2 text-sm text-red-500">
                                        Password is incorrect, please try again.
                                    </p>
                                )}
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="btn btn-default"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => handleSubmit()}
                                    isDisabled={!password || isProcessing}
                                    isLoading={isProcessing}
                                    loadingText='Processing...'
                                    className="btn btn-primary"
                                >
                                    {action === "enabled" ? "Enable" : "Disable"}
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </>
        );
    };

export default TwoFactorConfirmModal;